// ============================================================================
// ThreeDViewer.jsx — Zenna 3D Design Suite
// Version 1.0.0
// Modes: Viewer, Cookie Cutter, Shadow Box, Book Nook
// Dependencies: three (npm install three)
// ============================================================================

import { useEffect, useRef, useState, useCallback } from 'react'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter.js'

// ============================================================================
// CONSTANTS
// ============================================================================

const MODES = {
  VIEWER: 'viewer',
  COOKIE_CUTTER: 'cookie-cutter',
  SHADOW_BOX: 'shadow-box',
  BOOK_NOOK: 'book-nook',
  RELIEF: 'relief'
}

const MODE_LABELS = {
  [MODES.VIEWER]: '👁️ Viewer',
  [MODES.COOKIE_CUTTER]: '🍪 Cookie Cutter',
  [MODES.SHADOW_BOX]: '🖼️ Shadow Box',
  [MODES.BOOK_NOOK]: '📚 Book Nook',
  [MODES.RELIEF]: '🪵 3D Engrave preview'
}

const ZENNA_COLORS = {
  violet: '#8B5CF6',
  cyan: '#06B6D4',
  emerald: '#10B981',
  cream: '#FFF8F0',
  dark: '#0F172A',
  darkPanel: '#1E293B',
  darkBorder: '#334155',
  darkSurface: '#1a1f35'
}

// Cookie cutter handle styles
const HANDLE_STYLES = {
  NONE: 'none',
  RIM: 'rim',
  BRIDGE: 'bridge'
}

// ============================================================================
// BOOK NOOK — LASER-CUT LAYER DESIGN SYSTEM
// ============================================================================

// --- Shape Primitives ---
// Return arrays of {x,y} points forming closed polygons, coords relative to panel center (0,0)

function bnRoundArch(cx, bottomY, w, h, steps = 24) {
  const pts = [], hw = w / 2, straightH = h * 0.55
  pts.push({ x: cx - hw, y: bottomY })
  pts.push({ x: cx - hw, y: bottomY + straightH })
  for (let i = 0; i <= steps; i++) {
    const angle = Math.PI - (Math.PI * i / steps)
    pts.push({ x: cx + Math.cos(angle) * hw, y: bottomY + straightH + Math.sin(angle) * (h - straightH) })
  }
  pts.push({ x: cx + hw, y: bottomY + straightH })
  pts.push({ x: cx + hw, y: bottomY })
  return pts
}

function bnGothicArch(cx, bottomY, w, h, steps = 24) {
  const pts = [], hw = w / 2, straightH = h * 0.4, peakY = bottomY + h, half = Math.floor(steps / 2)
  pts.push({ x: cx - hw, y: bottomY })
  pts.push({ x: cx - hw, y: bottomY + straightH })
  for (let i = 1; i <= half; i++) {
    const t = i / half
    const x = (1 - t) * (1 - t) * (cx - hw) + 2 * (1 - t) * t * (cx - hw * 0.12) + t * t * cx
    const y = (1 - t) * (1 - t) * (bottomY + straightH) + 2 * (1 - t) * t * (peakY + h * 0.06) + t * t * peakY
    pts.push({ x, y })
  }
  for (let i = 1; i <= half; i++) {
    const t = i / half
    const x = (1 - t) * (1 - t) * cx + 2 * (1 - t) * t * (cx + hw * 0.12) + t * t * (cx + hw)
    const y = (1 - t) * (1 - t) * peakY + 2 * (1 - t) * t * (peakY + h * 0.06) + t * t * (bottomY + straightH)
    pts.push({ x, y })
  }
  pts.push({ x: cx + hw, y: bottomY })
  return pts
}

function bnRect(cx, cy, w, h) {
  return [
    { x: cx - w / 2, y: cy - h / 2 }, { x: cx + w / 2, y: cy - h / 2 },
    { x: cx + w / 2, y: cy + h / 2 }, { x: cx - w / 2, y: cy + h / 2 }
  ]
}

function bnCircle(cx, cy, r, steps = 16) {
  const pts = []
  for (let i = 0; i < steps; i++) pts.push({ x: cx + Math.cos(2 * Math.PI * i / steps) * r, y: cy + Math.sin(2 * Math.PI * i / steps) * r })
  return pts
}

function bnStar(cx, cy, outerR, innerR, points = 5) {
  const pts = []
  for (let i = 0; i < points * 2; i++) {
    const angle = (Math.PI / 2) + (Math.PI * i / points)
    const r = i % 2 === 0 ? outerR : innerR
    pts.push({ x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r })
  }
  return pts
}

function bnCrescent(cx, cy, r, steps = 20) {
  const pts = []
  for (let i = 0; i <= steps; i++) {
    const a = -Math.PI * 0.3 + Math.PI * 1.6 * (i / steps)
    pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r })
  }
  for (let i = steps; i >= 0; i--) {
    const a = -Math.PI * 0.3 + Math.PI * 1.6 * (i / steps)
    pts.push({ x: cx + r * 0.35 + Math.cos(a) * r * 0.75, y: cy + Math.sin(a) * r * 0.85 })
  }
  return pts
}

function bnShelfOpenings(cx, bottomY, w, h, numShelves = 3) {
  const cutouts = [], shelfH = h / (numShelves + 0.5), inset = w * 0.08
  for (let i = 0; i < numShelves; i++) {
    const cy = bottomY + (i + 0.65) * shelfH
    cutouts.push(bnRect(cx, cy, w - inset * 2, shelfH * 0.65))
  }
  return cutouts
}

function bnWindowPanes(cx, cy, w, h, rows = 2, cols = 2, gap = 3) {
  const cutouts = [], paneW = (w - gap * (cols + 1)) / cols, paneH = (h - gap * (rows + 1)) / rows
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const px = cx - w / 2 + gap + (c + 0.5) * (paneW + gap)
      const py = cy - h / 2 + gap + (r + 0.5) * (paneH + gap)
      cutouts.push(bnRect(px, py, paneW, paneH))
    }
  }
  return cutouts
}

function bnStaircaseCutout(cx, bottomY, w, h, numSteps = 6) {
  const pts = [], stepW = w / numSteps, stepH = h / numSteps
  pts.push({ x: cx - w / 2, y: bottomY })
  for (let i = 0; i < numSteps; i++) {
    pts.push({ x: cx - w / 2 + i * stepW, y: bottomY + (i + 1) * stepH })
    pts.push({ x: cx - w / 2 + (i + 1) * stepW, y: bottomY + (i + 1) * stepH })
  }
  pts.push({ x: cx + w / 2, y: bottomY })
  return pts
}

function bnTreeShape(cx, bottomY, trunkW, totalH, canopyW, canopyH, steps = 20) {
  const pts = [], trunkH = totalH - canopyH, canopyBottom = bottomY + trunkH, canopyCY = canopyBottom + canopyH * 0.5
  pts.push({ x: cx - trunkW / 2, y: bottomY })
  pts.push({ x: cx - trunkW / 2, y: canopyBottom })
  for (let i = 0; i <= steps; i++) {
    const a = Math.PI - (Math.PI * i / steps)
    const wobble = 1 + 0.12 * Math.sin(a * 3)
    pts.push({ x: cx + Math.cos(a) * canopyW / 2 * wobble, y: canopyCY + Math.sin(a) * canopyH / 2 * wobble })
  }
  pts.push({ x: cx + trunkW / 2, y: canopyBottom })
  pts.push({ x: cx + trunkW / 2, y: bottomY })
  return pts
}

function bnScallopEdge(x1, x2, topY, scH, numScallops = 5, steps = 10) {
  const pts = [], segW = (x2 - x1) / numScallops
  pts.push({ x: x1, y: topY })
  for (let s = 0; s < numScallops; s++) {
    const sx = x1 + s * segW
    for (let i = 0; i <= steps; i++) {
      const a = Math.PI * (i / steps)
      pts.push({ x: sx + segW * (i / steps), y: topY - Math.sin(a) * scH })
    }
  }
  pts.push({ x: x2, y: topY })
  return pts
}

// --- Theme Definitions ---
// Each theme returns layers of cutouts that scale with panel dimensions

const BOOK_NOOK_DESIGNS = {
  library: {
    name: '📖 Cozy Library',
    description: 'Arched windows, bookshelves, reading nook',
    color: 0x8B7355,
    getLayers: (w, h) => {
      const hw = w / 2, hh = h / 2
      return [
        { name: 'Front Frame', cutouts: [
          bnRoundArch(0, -hh + h * 0.04, w * 0.7, h * 0.88),
          bnCircle(-hw + w * 0.1, hh - h * 0.06, w * 0.035),
          bnCircle(hw - w * 0.1, hh - h * 0.06, w * 0.035),
          bnCircle(-hw + w * 0.1, -hh + h * 0.06, w * 0.025),
          bnCircle(hw - w * 0.1, -hh + h * 0.06, w * 0.025),
        ]},
        { name: 'Bookshelves', cutouts: [
          bnRect(0, 0, w * 0.45, h * 0.88),
          ...bnShelfOpenings(-hw + w * 0.16, -hh + h * 0.06, w * 0.2, h * 0.78, 4),
          ...bnShelfOpenings(hw - w * 0.16, -hh + h * 0.06, w * 0.2, h * 0.78, 4),
        ]},
        { name: 'Reading Nook', cutouts: [
          bnRoundArch(0, -hh + h * 0.28, w * 0.52, h * 0.65),
          bnRect(-w * 0.22, -hh + h * 0.1, w * 0.28, h * 0.18),
          bnRect(w * 0.22, -hh + h * 0.1, w * 0.2, h * 0.14),
        ]},
        { name: 'Back Arch', cutouts: [
          bnRoundArch(0, -hh + h * 0.02, w * 0.82, h * 0.92),
        ]},
      ]
    }
  },

  wizard: {
    name: '🧙 Wizard Study',
    description: 'Gothic arches, potion shelves, spiral staircase',
    color: 0x3E2723,
    getLayers: (w, h) => {
      const hw = w / 2, hh = h / 2
      return [
        { name: 'Gothic Frame', cutouts: [
          bnGothicArch(0, -hh + h * 0.04, w * 0.68, h * 0.9),
          bnStar(-hw + w * 0.1, hh - h * 0.08, w * 0.035, w * 0.015),
          bnStar(hw - w * 0.1, hh - h * 0.08, w * 0.035, w * 0.015),
          bnCrescent(0, hh - h * 0.06, w * 0.04),
        ]},
        { name: 'Potion Shelves', cutouts: [
          bnRect(0, h * 0.05, w * 0.42, h * 0.75),
          ...bnShelfOpenings(-hw + w * 0.14, -hh + h * 0.25, w * 0.17, h * 0.55, 3),
          bnStaircaseCutout(hw - w * 0.17, -hh + h * 0.04, w * 0.22, h * 0.5, 7),
        ]},
        { name: 'Wizard Window', cutouts: [
          bnGothicArch(0, -hh + h * 0.32, w * 0.45, h * 0.6),
          bnCircle(-w * 0.28, -hh + h * 0.15, w * 0.08),
          bnCircle(w * 0.28, -hh + h * 0.15, w * 0.08),
        ]},
        { name: 'Inner Arch', cutouts: [
          bnGothicArch(0, -hh + h * 0.02, w * 0.78, h * 0.93),
        ]},
      ]
    }
  },

  forest: {
    name: '🌲 Enchanted Forest',
    description: 'Tree trunks, canopy, mushrooms, forest path',
    color: 0x5D4037,
    getLayers: (w, h) => {
      const hw = w / 2, hh = h / 2
      return [
        { name: 'Tree Frame', cutouts: [
          // Large organic opening between two tree trunks with canopy
          (() => {
            const pts = [], steps = 30
            // Left trunk inner edge going up
            pts.push({ x: -w * 0.28, y: -hh })
            pts.push({ x: -w * 0.25, y: -hh + h * 0.5 })
            pts.push({ x: -w * 0.3, y: -hh + h * 0.6 })
            // Canopy arch with organic wobble
            for (let i = 0; i <= steps; i++) {
              const t = i / steps, a = Math.PI - Math.PI * t
              const wobble = 1 + 0.08 * Math.sin(a * 4)
              pts.push({ x: Math.cos(a) * w * 0.32 * wobble, y: hh - h * 0.12 + Math.sin(a) * h * 0.2 * wobble })
            }
            pts.push({ x: w * 0.3, y: -hh + h * 0.6 })
            pts.push({ x: w * 0.25, y: -hh + h * 0.5 })
            pts.push({ x: w * 0.28, y: -hh })
            return pts
          })(),
          // Leaf cutouts in canopy frame
          bnCircle(-w * 0.38, hh - h * 0.15, w * 0.03),
          bnCircle(w * 0.38, hh - h * 0.15, w * 0.03),
          bnCircle(-w * 0.42, hh - h * 0.28, w * 0.025),
          bnCircle(w * 0.42, hh - h * 0.28, w * 0.025),
        ]},
        { name: 'Deep Forest', cutouts: [
          bnRect(0, h * 0.05, w * 0.5, h * 0.78),
          // Mushroom-shaped cutout left
          bnCircle(-w * 0.32, -hh + h * 0.14, w * 0.06),
          // Mushroom-shaped cutout right
          bnCircle(w * 0.32, -hh + h * 0.1, w * 0.05),
        ]},
        { name: 'Forest Path', cutouts: [
          bnRoundArch(0, -hh + h * 0.1, w * 0.6, h * 0.78),
          // Small animal silhouette hole (rabbit-like circle)
          bnCircle(-w * 0.32, -hh + h * 0.2, w * 0.04),
        ]},
        { name: 'Tree Line', cutouts: [
          // Wide opening with distant tree silhouette effect at top
          bnRoundArch(0, -hh + h * 0.02, w * 0.85, h * 0.92),
        ]},
      ]
    }
  },

  cafe: {
    name: '☕ Parisian Café',
    description: 'Bistro awning, tables, warm interior',
    color: 0xA1887F,
    getLayers: (w, h) => {
      const hw = w / 2, hh = h / 2
      // Scallop awning cutout at top
      const awningCutout = (() => {
        const pts = [], numSc = 5, scH = h * 0.06, topY = hh - h * 0.02
        pts.push({ x: -w * 0.4, y: topY })
        pts.push({ x: -w * 0.4, y: topY - scH * 1.5 })
        const segW = w * 0.8 / numSc
        for (let s = 0; s < numSc; s++) {
          const sx = -w * 0.4 + s * segW
          for (let i = 0; i <= 10; i++) {
            const a = Math.PI + Math.PI * (i / 10)
            pts.push({ x: sx + segW * (i / 10), y: topY - scH * 1.5 + Math.sin(a) * scH * 0.5 })
          }
        }
        pts.push({ x: w * 0.4, y: topY - scH * 1.5 })
        pts.push({ x: w * 0.4, y: topY })
        return pts
      })()
      return [
        { name: 'Café Front', cutouts: [
          bnRect(0, -h * 0.05, w * 0.65, h * 0.72),
          awningCutout,
        ]},
        { name: 'Tables & Counter', cutouts: [
          bnRect(0, h * 0.1, w * 0.48, h * 0.65),
          bnRect(-w * 0.3, -hh + h * 0.15, w * 0.18, h * 0.22),
          bnRect(w * 0.3, -hh + h * 0.12, w * 0.16, h * 0.18),
        ]},
        { name: 'Window Display', cutouts: [
          ...bnWindowPanes(0, h * 0.08, w * 0.44, h * 0.45, 2, 2, 4),
          bnRect(0, -hh + h * 0.12, w * 0.35, h * 0.16),
        ]},
        { name: 'Interior', cutouts: [
          bnRoundArch(0, -hh + h * 0.02, w * 0.82, h * 0.9),
        ]},
      ]
    }
  },

  space: {
    name: '🚀 Space Station',
    description: 'Viewport windows, corridor panels, tech details',
    color: 0x37474F,
    getLayers: (w, h) => {
      const hw = w / 2, hh = h / 2
      return [
        { name: 'Airlock Frame', cutouts: [
          // Rounded-corner viewport
          bnRoundArch(0, -hh + h * 0.04, w * 0.65, h * 0.85),
          // Angular detail cutouts in corners
          bnRect(-hw + w * 0.06, hh - h * 0.05, w * 0.06, h * 0.04),
          bnRect(hw - w * 0.06, hh - h * 0.05, w * 0.06, h * 0.04),
          bnRect(-hw + w * 0.06, -hh + h * 0.05, w * 0.06, h * 0.04),
          bnRect(hw - w * 0.06, -hh + h * 0.05, w * 0.06, h * 0.04),
        ]},
        { name: 'Corridor Panels', cutouts: [
          bnRect(0, h * 0.05, w * 0.48, h * 0.78),
          // Left panel openings
          bnRect(-hw + w * 0.13, hh - h * 0.2, w * 0.12, h * 0.12),
          bnRect(-hw + w * 0.13, 0, w * 0.12, h * 0.12),
          bnRect(-hw + w * 0.13, -hh + h * 0.2, w * 0.12, h * 0.12),
          // Right panel openings
          bnRect(hw - w * 0.13, hh - h * 0.2, w * 0.12, h * 0.12),
          bnRect(hw - w * 0.13, 0, w * 0.12, h * 0.12),
          bnRect(hw - w * 0.13, -hh + h * 0.2, w * 0.12, h * 0.12),
        ]},
        { name: 'Viewport', cutouts: [
          bnCircle(0, h * 0.08, w * 0.22, 24),
          bnRect(-w * 0.3, -hh + h * 0.12, w * 0.15, h * 0.12),
          bnRect(w * 0.3, -hh + h * 0.12, w * 0.15, h * 0.12),
        ]},
        { name: 'Deep Space', cutouts: [
          bnRect(0, 0, w * 0.85, h * 0.9),
        ]},
      ]
    }
  },

  garden: {
    name: '🌸 Secret Garden',
    description: 'Trellis arch, climbing flowers, stone path',
    color: 0x6D4C41,
    getLayers: (w, h) => {
      const hw = w / 2, hh = h / 2
      return [
        { name: 'Garden Arch', cutouts: [
          bnRoundArch(0, -hh + h * 0.04, w * 0.62, h * 0.88),
          // Flower cutouts in frame
          bnCircle(-hw + w * 0.1, hh - h * 0.1, w * 0.03),
          bnCircle(hw - w * 0.1, hh - h * 0.1, w * 0.03),
          bnCircle(-hw + w * 0.08, hh - h * 0.22, w * 0.025),
          bnCircle(hw - w * 0.08, hh - h * 0.22, w * 0.025),
          bnCircle(-hw + w * 0.12, hh - h * 0.35, w * 0.02),
          bnCircle(hw - w * 0.12, hh - h * 0.35, w * 0.02),
          // Trellis diamond cutouts
          bnRect(-hw + w * 0.06, 0, w * 0.04, w * 0.04),
          bnRect(hw - w * 0.06, 0, w * 0.04, w * 0.04),
        ]},
        { name: 'Gate & Posts', cutouts: [
          bnRoundArch(0, -hh + h * 0.08, w * 0.5, h * 0.75),
          bnRect(-w * 0.32, -hh + h * 0.12, w * 0.08, h * 0.35),
          bnRect(w * 0.32, -hh + h * 0.12, w * 0.08, h * 0.35),
        ]},
        { name: 'Flowers & Bench', cutouts: [
          bnRect(0, h * 0.05, w * 0.55, h * 0.7),
          bnCircle(-w * 0.33, -hh + h * 0.18, w * 0.06),
          bnCircle(w * 0.33, -hh + h * 0.18, w * 0.06),
        ]},
        { name: 'Garden Backdrop', cutouts: [
          bnRoundArch(0, -hh + h * 0.02, w * 0.85, h * 0.92),
        ]},
      ]
    }
  }
}

// ============================================================================
// CONTOUR TRACING — Marching Squares Algorithm
// ============================================================================

function imageToGrid(imageData, width, height, threshold = 128) {
  // Create binary grid: 1 = filled (dark+opaque), 0 = empty
  const grid = new Uint8Array((width + 2) * (height + 2))
  const w = width + 2

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4
      const r = imageData[i], g = imageData[i + 1], b = imageData[i + 2], a = imageData[i + 3]
      // Pixel is "filled" if opaque enough and dark enough
      grid[(y + 1) * w + (x + 1)] = (a > 100 && (r + g + b) / 3 < threshold) ? 1 : 0
    }
  }
  return { grid, w, h: height + 2 }
}

function marchingSquares(grid, w, h) {
  const contours = []
  const visited = new Set()

  // Marching squares: walk the boundary between filled and empty cells
  for (let y = 0; y < h - 1; y++) {
    for (let x = 0; x < w - 1; x++) {
      // Cell corners: top-left, top-right, bottom-right, bottom-left
      const tl = grid[y * w + x]
      const tr = grid[y * w + x + 1]
      const br = grid[(y + 1) * w + x + 1]
      const bl = grid[(y + 1) * w + x]
      const cellType = (tl << 3) | (tr << 2) | (br << 1) | bl

      // Only start tracing from boundary cells not yet visited
      if (cellType === 0 || cellType === 15) continue
      const key = `${x},${y}`
      if (visited.has(key)) continue

      // Trace contour from this starting point
      const contour = traceContour(grid, w, h, x, y, visited)
      if (contour.length > 10) contours.push(contour)
    }
  }

  return contours
}

function traceContour(grid, w, h, startX, startY, visited) {
  const points = []
  let x = startX, y = startY
  let prevDir = 0
  const maxSteps = w * h * 2

  for (let step = 0; step < maxSteps; step++) {
    const key = `${x},${y}`
    visited.add(key)

    const tl = (y >= 0 && x >= 0) ? grid[y * w + x] : 0
    const tr = (y >= 0 && x + 1 < w) ? grid[y * w + x + 1] : 0
    const br = (y + 1 < h && x + 1 < w) ? grid[(y + 1) * w + x + 1] : 0
    const bl = (y + 1 < h && x >= 0) ? grid[(y + 1) * w + x] : 0
    const cellType = (tl << 3) | (tr << 2) | (br << 1) | bl

    // Midpoint of cell edges based on configuration
    let px = x + 0.5, py = y + 0.5
    points.push({ x: px, y: py })

    // Direction: 0=right, 1=down, 2=left, 3=up
    let dir
    switch (cellType) {
      case 1: case 5: case 13: dir = 3; break   // up
      case 2: case 3: case 7: dir = 0; break    // right
      case 4: case 12: case 14: dir = 1; break  // down
      case 8: case 10: case 11: dir = 2; break  // left
      case 6: dir = (prevDir === 3) ? 0 : 2; break  // ambiguous: saddle
      case 9: dir = (prevDir === 0) ? 3 : 1; break  // ambiguous: saddle
      default: dir = prevDir; break
    }

    prevDir = dir
    switch (dir) {
      case 0: x++; break
      case 1: y++; break
      case 2: x--; break
      case 3: y--; break
    }

    // Check if we've returned to start
    if (x === startX && y === startY && step > 2) break
    if (x < -1 || x > w || y < -1 || y > h) break
  }

  return points
}

// ============================================================================
// PATH SIMPLIFICATION — Douglas-Peucker Algorithm
// ============================================================================

function simplifyPath(points, tolerance = 1.5) {
  if (points.length < 3) return points

  const sqDist = (p1, p2) => (p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2
  const perpDist = (p, a, b) => {
    const dx = b.x - a.x, dy = b.y - a.y
    const lenSq = dx * dx + dy * dy
    if (lenSq === 0) return Math.sqrt(sqDist(p, a))
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq))
    return Math.sqrt(sqDist(p, { x: a.x + t * dx, y: a.y + t * dy }))
  }

  function simplify(pts, start, end, tol) {
    let maxDist = 0, maxIdx = start
    for (let i = start + 1; i < end; i++) {
      const d = perpDist(pts[i], pts[start], pts[end])
      if (d > maxDist) { maxDist = d; maxIdx = i }
    }
    if (maxDist > tol) {
      const left = simplify(pts, start, maxIdx, tol)
      const right = simplify(pts, maxIdx, end, tol)
      return [...left.slice(0, -1), ...right]
    }
    return [pts[start], pts[end]]
  }

  return simplify(points, 0, points.length - 1, tolerance)
}

// ============================================================================
// COOKIE CUTTER GEOMETRY GENERATOR
// ============================================================================

function createCookieCutterMesh(contour, options = {}) {
  const {
    height = 15,       // mm — cutter wall height
    thickness = 1.2,   // mm — wall thickness
    handleStyle = HANDLE_STYLES.RIM,
    handleHeight = 5,  // mm
    scale = 1.0
  } = options

  const group = new THREE.Group()

  // Normalize contour to center and scale
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
  for (const p of contour) {
    minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x)
    minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y)
  }
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2
  const contourWidth = maxX - minX, contourHeight = maxY - minY
  const maxDim = Math.max(contourWidth, contourHeight)
  const normScale = (80 * scale) / maxDim // Normalize to ~80mm

  const normalized = contour.map(p => ({
    x: (p.x - cx) * normScale,
    y: (p.y - cy) * normScale
  }))

  // Create outer shape
  const outerShape = new THREE.Shape()
  outerShape.moveTo(normalized[0].x, normalized[0].y)
  for (let i = 1; i < normalized.length; i++) {
    outerShape.lineTo(normalized[i].x, normalized[i].y)
  }
  outerShape.closePath()

  // Create inner shape (offset inward by thickness)
  const innerPoints = offsetPath(normalized, -thickness)
  if (innerPoints.length > 2) {
    const hole = new THREE.Path()
    hole.moveTo(innerPoints[0].x, innerPoints[0].y)
    for (let i = 1; i < innerPoints.length; i++) {
      hole.lineTo(innerPoints[i].x, innerPoints[i].y)
    }
    hole.closePath()
    outerShape.holes.push(hole)
  }

  // Extrude the walls
  const wallGeo = new THREE.ExtrudeGeometry(outerShape, {
    depth: height,
    bevelEnabled: false
  })
  const wallMat = new THREE.MeshPhongMaterial({
    color: 0xE0E0E0,
    specular: 0x444444,
    shininess: 60
  })
  const wallMesh = new THREE.Mesh(wallGeo, wallMat)
  wallMesh.rotation.x = -Math.PI / 2
  group.add(wallMesh)

  // Add handle
  if (handleStyle === HANDLE_STYLES.RIM) {
    // Rim handle: thicker ring at the top
    const rimShape = new THREE.Shape()
    const rimOuter = offsetPath(normalized, thickness)
    rimShape.moveTo(rimOuter[0].x, rimOuter[0].y)
    for (let i = 1; i < rimOuter.length; i++) {
      rimShape.lineTo(rimOuter[i].x, rimOuter[i].y)
    }
    rimShape.closePath()

    const rimHole = new THREE.Path()
    rimHole.moveTo(innerPoints[0].x, innerPoints[0].y)
    for (let i = 1; i < innerPoints.length; i++) {
      rimHole.lineTo(innerPoints[i].x, innerPoints[i].y)
    }
    rimHole.closePath()
    rimShape.holes.push(rimHole)

    const rimGeo = new THREE.ExtrudeGeometry(rimShape, {
      depth: handleHeight,
      bevelEnabled: false
    })
    const rimMesh = new THREE.Mesh(rimGeo, wallMat.clone())
    rimMesh.material.color.set(0xB0B0B0)
    rimMesh.rotation.x = -Math.PI / 2
    rimMesh.position.y = height
    group.add(rimMesh)
  } else if (handleStyle === HANDLE_STYLES.BRIDGE) {
    // Bridge handle: bar across the top
    const bridgeGeo = new THREE.BoxGeometry(
      Math.min(40, (maxX - minX) * normScale * 0.6),
      handleHeight,
      thickness * 2
    )
    const bridgeMesh = new THREE.Mesh(bridgeGeo, wallMat.clone())
    bridgeMesh.material.color.set(0xB0B0B0)
    bridgeMesh.position.y = height + handleHeight / 2
    group.add(bridgeMesh)
  }

  return group
}

// Offset a closed 2D path inward (negative) or outward (positive)
function offsetPath(points, offset) {
  if (points.length < 3) return points
  const result = []

  for (let i = 0; i < points.length; i++) {
    const prev = points[(i - 1 + points.length) % points.length]
    const curr = points[i]
    const next = points[(i + 1) % points.length]

    // Edge normals
    const dx1 = curr.x - prev.x, dy1 = curr.y - prev.y
    const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1) || 1
    const nx1 = -dy1 / len1, ny1 = dx1 / len1

    const dx2 = next.x - curr.x, dy2 = next.y - curr.y
    const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2) || 1
    const nx2 = -dy2 / len2, ny2 = dx2 / len2

    // Average normal at vertex
    let nx = (nx1 + nx2) / 2, ny = (ny1 + ny2) / 2
    const nLen = Math.sqrt(nx * nx + ny * ny) || 1
    nx /= nLen; ny /= nLen

    // Miter factor (clamped to avoid spikes)
    const dot = nx1 * nx + ny1 * ny
    const miter = Math.min(3, 1 / (dot || 0.01))

    result.push({
      x: curr.x + nx * offset * miter,
      y: curr.y + ny * offset * miter
    })
  }

  return result
}

// ============================================================================
// SHADOW BOX LAYER SEPARATION
// ============================================================================

function separateImageLayers(imageData, width, height, numLayers = 4) {
  const layers = Array.from({ length: numLayers }, () => [])

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4
      const r = imageData[i], g = imageData[i + 1], b = imageData[i + 2], a = imageData[i + 3]

      if (a < 50) continue // Skip transparent pixels

      // Brightness-based layer assignment
      const brightness = (r * 0.299 + g * 0.587 + b * 0.114) / 255
      const layerIdx = Math.min(numLayers - 1, Math.floor(brightness * numLayers))
      layers[layerIdx].push({ x, y, r, g, b })
    }
  }

  return layers
}

function createShadowBoxMesh(imageData, width, height, options = {}) {
  const {
    numLayers = 4,
    layerSpacing = 8,   // mm between layers
    frameWidth = 12,     // mm frame border
    frameDepth = 0,      // auto-calculated
    showFrame = true,
    boxWidth = 150,      // mm total width
    boxHeight = 150      // mm total height
  } = options

  const group = new THREE.Group()
  const layers = separateImageLayers(imageData, width, height, numLayers)
  const scaleX = boxWidth / width
  const scaleY = boxHeight / height
  const totalDepth = frameDepth || (numLayers * layerSpacing + 10)

  // Colors for each layer (darker = deeper)
  const layerColors = [
    0x1a1a2e, 0x2d2d44, 0x45455c, 0x606078,
    0x8080a0, 0xa0a0c0
  ]

  layers.forEach((layerPixels, idx) => {
    if (layerPixels.length === 0) return

    // Create a plane with pixel data as texture
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')

    // Fill transparent first
    ctx.clearRect(0, 0, width, height)

    // Draw only this layer's pixels
    const imgData = ctx.createImageData(width, height)
    for (const p of layerPixels) {
      const i = (p.y * width + p.x) * 4
      imgData.data[i] = p.r
      imgData.data[i + 1] = p.g
      imgData.data[i + 2] = p.b
      imgData.data[i + 3] = 255
    }
    ctx.putImageData(imgData, 0, 0)

    const texture = new THREE.CanvasTexture(canvas)
    texture.minFilter = THREE.LinearFilter

    const planeGeo = new THREE.PlaneGeometry(boxWidth, boxHeight)
    const planeMat = new THREE.MeshPhongMaterial({
      map: texture,
      transparent: true,
      alphaTest: 0.1,
      side: THREE.DoubleSide
    })
    const planeMesh = new THREE.Mesh(planeGeo, planeMat)
    planeMesh.position.z = -idx * layerSpacing
    planeMesh.name = `layer-${idx}`
    group.add(planeMesh)
  })

  // Frame
  if (showFrame) {
    const frameMat = new THREE.MeshPhongMaterial({
      color: 0x3E2723,
      specular: 0x111111,
      shininess: 30
    })

    const halfW = boxWidth / 2 + frameWidth
    const halfH = boxHeight / 2 + frameWidth

    // Top frame piece
    const topGeo = new THREE.BoxGeometry(boxWidth + frameWidth * 2, frameWidth, totalDepth)
    const topMesh = new THREE.Mesh(topGeo, frameMat)
    topMesh.position.set(0, boxHeight / 2 + frameWidth / 2, -totalDepth / 2 + layerSpacing)
    group.add(topMesh)

    // Bottom frame piece
    const botMesh = new THREE.Mesh(topGeo, frameMat)
    botMesh.position.set(0, -boxHeight / 2 - frameWidth / 2, -totalDepth / 2 + layerSpacing)
    group.add(botMesh)

    // Left frame piece
    const sideGeo = new THREE.BoxGeometry(frameWidth, boxHeight, totalDepth)
    const leftMesh = new THREE.Mesh(sideGeo, frameMat)
    leftMesh.position.set(-boxWidth / 2 - frameWidth / 2, 0, -totalDepth / 2 + layerSpacing)
    group.add(leftMesh)

    // Right frame piece
    const rightMesh = new THREE.Mesh(sideGeo, frameMat)
    rightMesh.position.set(boxWidth / 2 + frameWidth / 2, 0, -totalDepth / 2 + layerSpacing)
    group.add(rightMesh)

    // Back plate
    const backGeo = new THREE.PlaneGeometry(boxWidth + frameWidth * 2, boxHeight + frameWidth * 2)
    const backMat = new THREE.MeshPhongMaterial({ color: 0x1a1a1a, side: THREE.DoubleSide })
    const backMesh = new THREE.Mesh(backGeo, backMat)
    backMesh.position.z = -totalDepth + layerSpacing
    group.add(backMesh)
  }

  return group
}

// ============================================================================
// BOOK NOOK — LAYER GEOMETRY GENERATOR
// ============================================================================

function pointsToThreePath(points) {
  const path = new THREE.Path()
  path.moveTo(points[0].x, points[0].y)
  for (let i = 1; i < points.length; i++) path.lineTo(points[i].x, points[i].y)
  path.closePath()
  return path
}

function createBookNookLayers(options = {}) {
  const {
    theme = 'library',
    width = 180,
    height = 220,
    depth = 150,
    materialThickness = 3,
    backImage = null,
    showLighting = true
  } = options

  const design = BOOK_NOOK_DESIGNS[theme] || BOOK_NOOK_DESIGNS.library
  const layers = design.getLayers(width, height)
  const group = new THREE.Group()

  const numLayers = layers.length
  const usableDepth = depth - materialThickness * (numLayers + 1) // +1 for back wall
  const spacerDepth = usableDepth / numLayers

  // Material colors — front layers lighter, back layers darker
  const baseColor = new THREE.Color(design.color)

  layers.forEach((layer, i) => {
    const zPos = depth / 2 - i * (spacerDepth + materialThickness) - materialThickness / 2

    // Outer rectangle shape
    const shape = new THREE.Shape()
    shape.moveTo(-width / 2, -height / 2)
    shape.lineTo(width / 2, -height / 2)
    shape.lineTo(width / 2, height / 2)
    shape.lineTo(-width / 2, height / 2)
    shape.closePath()

    // Add cutout holes
    for (const cutout of layer.cutouts) {
      shape.holes.push(pointsToThreePath(cutout))
    }

    // Extrude the layer
    const geo = new THREE.ExtrudeGeometry(shape, { depth: materialThickness, bevelEnabled: false })
    const layerColor = baseColor.clone()
    layerColor.offsetHSL(0, 0, -0.04 * i) // slightly darker each layer back

    const mat = new THREE.MeshPhongMaterial({
      color: layerColor,
      side: THREE.DoubleSide,
      flatShading: false,
    })
    const mesh = new THREE.Mesh(geo, mat)
    mesh.position.z = zPos
    mesh.name = layer.name
    group.add(mesh)
  })

  // Back wall
  const backZ = -depth / 2 + materialThickness / 2
  const backGeo = new THREE.BoxGeometry(width, height, materialThickness)
  let backMat
  if (backImage) {
    const tex = new THREE.TextureLoader().load(backImage)
    tex.colorSpace = THREE.SRGBColorSpace
    backMat = new THREE.MeshPhongMaterial({ map: tex, side: THREE.DoubleSide })
  } else {
    const darkColor = baseColor.clone().offsetHSL(0, 0, -0.15)
    backMat = new THREE.MeshPhongMaterial({ color: darkColor, side: THREE.DoubleSide })
  }
  const backWall = new THREE.Mesh(backGeo, backMat)
  backWall.position.z = backZ
  backWall.name = 'Back Wall'
  group.add(backWall)

  // Semi-transparent side walls for visual reference
  const sideGeo = new THREE.BoxGeometry(materialThickness, height, depth)
  const sideMat = new THREE.MeshPhongMaterial({
    color: design.color, transparent: true, opacity: 0.2, side: THREE.DoubleSide
  })
  const leftWall = new THREE.Mesh(sideGeo, sideMat.clone())
  leftWall.position.x = -width / 2 - materialThickness / 2
  leftWall.name = 'Left Wall'
  group.add(leftWall)
  const rightWall = new THREE.Mesh(sideGeo, sideMat.clone())
  rightWall.position.x = width / 2 + materialThickness / 2
  rightWall.name = 'Right Wall'
  group.add(rightWall)

  // Floor and ceiling (thin, transparent)
  const floorGeo = new THREE.BoxGeometry(width, materialThickness, depth)
  const floorMat = new THREE.MeshPhongMaterial({
    color: design.color, transparent: true, opacity: 0.15, side: THREE.DoubleSide
  })
  const floor = new THREE.Mesh(floorGeo, floorMat.clone())
  floor.position.y = -height / 2 - materialThickness / 2
  group.add(floor)
  const ceiling = new THREE.Mesh(floorGeo, floorMat.clone())
  ceiling.position.y = height / 2 + materialThickness / 2
  group.add(ceiling)

  // Interior lighting
  if (showLighting) {
    const light1 = new THREE.PointLight(0xFFD700, 40, depth * 2)
    light1.position.set(0, height * 0.35, depth * 0.1)
    group.add(light1)
    const light2 = new THREE.PointLight(0xFFF0D0, 25, depth * 1.5)
    light2.position.set(0, -height * 0.1, -depth * 0.2)
    group.add(light2)
  }

  return group
}

// ============================================================================
// BOOK NOOK SVG EXPORT — Laser Cutter Ready
// ============================================================================

function exportBookNookSVGs({ theme, width, height, depth, materialThickness = 3 }) {
  const design = BOOK_NOOK_DESIGNS[theme] || BOOK_NOOK_DESIGNS.library
  const layers = design.getLayers(width, height)
  const numLayers = layers.length
  const usableDepth = depth - materialThickness * (numLayers + 1)
  const spacerDepth = (usableDepth / numLayers).toFixed(1)

  // Layout: all layers side by side on one SVG sheet
  const margin = 10
  const totalW = (width + margin) * (numLayers + 1) + margin // +1 for back wall
  const totalH = height + margin * 4 + 20 // extra for labels

  let allPaths = ''

  layers.forEach((layer, i) => {
    const offsetX = margin + i * (width + margin)
    const offsetY = margin

    // Layer label (engrave — blue)
    allPaths += `  <text x="${offsetX + width / 2}" y="${offsetY + height + 12}" text-anchor="middle" font-family="Arial" font-size="4" fill="#0000FF">Layer ${i + 1}: ${layer.name} | Spacer: ${spacerDepth}mm</text>\n`

    // Outer rectangle (cut — red)
    allPaths += `  <rect x="${offsetX}" y="${offsetY}" width="${width}" height="${height}" fill="none" stroke="#FF0000" stroke-width="0.2"/>\n`

    // Cutout paths (cut — red)
    for (const cutout of layer.cutouts) {
      let d = `M ${(cutout[0].x + width / 2 + offsetX).toFixed(2)} ${(height / 2 - cutout[0].y + offsetY).toFixed(2)}`
      for (let j = 1; j < cutout.length; j++) {
        d += ` L ${(cutout[j].x + width / 2 + offsetX).toFixed(2)} ${(height / 2 - cutout[j].y + offsetY).toFixed(2)}`
      }
      d += ' Z'
      allPaths += `  <path d="${d}" fill="none" stroke="#FF0000" stroke-width="0.2"/>\n`
    }
  })

  // Back wall panel
  const backOffsetX = margin + numLayers * (width + margin)
  allPaths += `  <text x="${backOffsetX + width / 2}" y="${margin + height + 12}" text-anchor="middle" font-family="Arial" font-size="4" fill="#0000FF">Back Wall — Print/attach canvas artwork</text>\n`
  allPaths += `  <rect x="${backOffsetX}" y="${margin}" width="${width}" height="${height}" fill="none" stroke="#FF0000" stroke-width="0.2"/>\n`

  // Side walls
  const sideY = margin + height + 25
  allPaths += `  <text x="${margin + depth / 2}" y="${sideY - 3}" text-anchor="middle" font-family="Arial" font-size="4" fill="#0000FF">Side Walls (×2) — ${depth}mm × ${height}mm</text>\n`
  allPaths += `  <rect x="${margin}" y="${sideY}" width="${depth}" height="${height}" fill="none" stroke="#FF0000" stroke-width="0.2"/>\n`

  const totalHFull = sideY + height + margin

  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg"
  width="${totalW}mm" height="${totalHFull}mm"
  viewBox="0 0 ${totalW} ${totalHFull}">
  <title>Book Nook: ${design.name} — ${width}×${height}×${depth}mm</title>
  <!-- Red = CUT lines | Blue = ENGRAVE / labels -->
  <!-- Material thickness: ${materialThickness}mm | Spacer depth: ${spacerDepth}mm -->
${allPaths}
</svg>`

  const blob = new Blob([svg], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `book-nook-${theme}-${width}x${height}x${depth}mm.svg`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  return `Exported ${numLayers} layers + back wall + side walls`
}

// ============================================================================
// STL EXPORT UTILITY
// ============================================================================

function exportSTL(scene, filename = 'zenna-export.stl') {
  const exporter = new STLExporter()
  const stlString = exporter.parse(scene, { binary: true })
  const blob = new Blob([stlString], { type: 'application/octet-stream' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ThreeDViewer({
  canvasRef = null,     // Fabric.js canvas reference
  canvasImage = null,   // Current canvas image URL
  initialMode = MODES.VIEWER,
  onModeChange = null
}) {
  // --- State ---
  const containerRef = useRef(null)
  const rendererRef = useRef(null)
  const sceneRef = useRef(null)
  const cameraRef = useRef(null)
  const controlsRef = useRef(null)
  const frameIdRef = useRef(null)
  const modelGroupRef = useRef(null)

  const [mode, setMode] = useState(initialMode)
  const [showGrid, setShowGrid] = useState(true)
  const [showWireframe, setShowWireframe] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')

  // Cookie cutter settings
  const [ccHeight, setCcHeight] = useState(15)
  const [ccThickness, setCcThickness] = useState(1.2)
  const [ccHandle, setCcHandle] = useState(HANDLE_STYLES.RIM)
  const [ccHandleHeight, setCcHandleHeight] = useState(5)
  const [ccScale, setCcScale] = useState(100)
  const [ccThreshold, setCcThreshold] = useState(128)

  // Shadow box settings
  const [sbLayers, setSbLayers] = useState(4)
  const [sbSpacing, setSbSpacing] = useState(8)
  const [sbFrame, setSbFrame] = useState(true)
  const [sbFrameWidth, setSbFrameWidth] = useState(12)
  const [sbSize, setSbSize] = useState(150)

  // 3D Engrave (relief) preview settings
  const [reliefMaxDepthMm, setReliefMaxDepthMm] = useState(3)
  const [reliefMaterial, setReliefMaterial] = useState('wood')  // 'wood' | 'acrylic' | 'slate'
  const [reliefAiMode, setReliefAiMode] = useState(false)
  const [reliefWidthMm, setReliefWidthMm] = useState(150)

  // Book nook settings
  const [bnTheme, setBnTheme] = useState('library')
  const [bnWidth, setBnWidth] = useState(180)
  const [bnHeight, setBnHeight] = useState(220)
  const [bnDepth, setBnDepth] = useState(150)
  const [bnLighting, setBnLighting] = useState(true)
  const [bnMaterialThickness, setBnMaterialThickness] = useState(3)

  // --- Three.js Scene Setup ---
  useEffect(() => {
    if (!containerRef.current) return

    const container = containerRef.current
    const width = container.clientWidth
    const height = container.clientHeight

    // Scene
    const scene = new THREE.Scene()
    scene.background = new THREE.Color(ZENNA_COLORS.darkSurface)
    sceneRef.current = scene

    // Camera
    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 5000)
    camera.position.set(120, 100, 200)
    cameraRef.current = camera

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    renderer.setSize(width, height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.shadowMap.enabled = true
    renderer.shadowMap.type = THREE.PCFSoftShadowMap
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = 1.2
    container.appendChild(renderer.domElement)
    rendererRef.current = renderer

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement)
    controls.enableDamping = true
    controls.dampingFactor = 0.08
    controls.minDistance = 20
    controls.maxDistance = 1000
    controlsRef.current = controls

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5)
    scene.add(ambientLight)

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0)
    directionalLight.position.set(100, 150, 100)
    directionalLight.castShadow = true
    scene.add(directionalLight)

    const fillLight = new THREE.DirectionalLight(0x8B5CF6, 0.3) // Zenna violet fill
    fillLight.position.set(-80, 50, -50)
    scene.add(fillLight)

    // Grid
    const gridHelper = new THREE.GridHelper(400, 40, 0x334155, 0x1E293B)
    gridHelper.name = 'grid'
    scene.add(gridHelper)

    // Axes (subtle)
    const axesHelper = new THREE.AxesHelper(50)
    axesHelper.name = 'axes'
    axesHelper.visible = false
    scene.add(axesHelper)

    // Model group (holds the active 3D content)
    const modelGroup = new THREE.Group()
    modelGroup.name = 'model'
    scene.add(modelGroup)
    modelGroupRef.current = modelGroup

    // Animation loop
    function animate() {
      frameIdRef.current = requestAnimationFrame(animate)
      controls.update()
      renderer.render(scene, camera)
    }
    animate()

    // Resize handler
    const onResize = () => {
      const w = container.clientWidth
      const h = container.clientHeight
      camera.aspect = w / h
      camera.updateProjectionMatrix()
      renderer.setSize(w, h)
    }
    const resizeObserver = new ResizeObserver(onResize)
    resizeObserver.observe(container)

    // Cleanup
    return () => {
      cancelAnimationFrame(frameIdRef.current)
      resizeObserver.disconnect()
      controls.dispose()
      renderer.dispose()
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement)
      }
    }
  }, [])

  // Toggle grid visibility
  useEffect(() => {
    if (!sceneRef.current) return
    const grid = sceneRef.current.getObjectByName('grid')
    if (grid) grid.visible = showGrid
  }, [showGrid])

  // Toggle wireframe
  useEffect(() => {
    if (!modelGroupRef.current) return
    modelGroupRef.current.traverse((child) => {
      if (child.isMesh && child.material) {
        child.material.wireframe = showWireframe
      }
    })
  }, [showWireframe])

  // --- Model Management ---
  const clearModel = useCallback(() => {
    if (!modelGroupRef.current) return
    while (modelGroupRef.current.children.length > 0) {
      const child = modelGroupRef.current.children[0]
      if (child.geometry) child.geometry.dispose()
      if (child.material) {
        if (child.material.map) child.material.map.dispose()
        child.material.dispose()
      }
      modelGroupRef.current.remove(child)
    }
  }, [])

  const resetCamera = useCallback((distance = 200) => {
    if (!cameraRef.current || !controlsRef.current) return
    cameraRef.current.position.set(distance * 0.6, distance * 0.5, distance)
    controlsRef.current.target.set(0, 0, 0)
    controlsRef.current.update()
  }, [])

  // --- Get canvas image data ---
  const getCanvasImageData = useCallback(() => {
    return new Promise((resolve, reject) => {
      // Try Fabric.js canvas first
      if (canvasRef?.current) {
        try {
          const fabricCanvas = canvasRef.current
          const dataUrl = fabricCanvas.toDataURL({ format: 'png', multiplier: 1 })
          const img = new Image()
          img.crossOrigin = 'anonymous'
          img.onload = () => {
            const tempCanvas = document.createElement('canvas')
            const ctx = tempCanvas.getContext('2d')
            // Resize to manageable resolution for tracing
            const maxDim = 256
            const scale = Math.min(maxDim / img.width, maxDim / img.height)
            tempCanvas.width = Math.floor(img.width * scale)
            tempCanvas.height = Math.floor(img.height * scale)
            ctx.drawImage(img, 0, 0, tempCanvas.width, tempCanvas.height)
            const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height)
            resolve({ data: imageData.data, width: tempCanvas.width, height: tempCanvas.height })
          }
          img.onerror = reject
          img.src = dataUrl
          return
        } catch (e) {
          // Fall through to canvasImage
        }
      }

      // Fallback: use canvasImage URL
      if (canvasImage) {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          const tempCanvas = document.createElement('canvas')
          const ctx = tempCanvas.getContext('2d')
          const maxDim = 256
          const scale = Math.min(maxDim / img.width, maxDim / img.height)
          tempCanvas.width = Math.floor(img.width * scale)
          tempCanvas.height = Math.floor(img.height * scale)
          ctx.drawImage(img, 0, 0, tempCanvas.width, tempCanvas.height)
          const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height)
          resolve({ data: imageData.data, width: tempCanvas.width, height: tempCanvas.height })
        }
        img.onerror = reject
        img.src = canvasImage
        return
      }

      reject(new Error('No canvas image available'))
    })
  }, [canvasRef, canvasImage])

  // --- Cookie Cutter Generation ---
  const generateCookieCutter = useCallback(async () => {
    setIsGenerating(true)
    setStatusMessage('Tracing outline...')

    try {
      const { data, width, height } = await getCanvasImageData()

      // Step 1: Trace contours
      const { grid, w, h } = imageToGrid(data, width, height, ccThreshold)
      const contours = marchingSquares(grid, w, h)

      if (contours.length === 0) {
        setStatusMessage('⚠️ No outline found. Try adjusting the threshold or use a design with clear edges.')
        setIsGenerating(false)
        return
      }

      // Use the longest contour (main shape)
      setStatusMessage('Building 3D model...')
      const mainContour = contours.reduce((a, b) => a.length > b.length ? a : b)
      const simplified = simplifyPath(mainContour, 1.0)

      clearModel()

      const mesh = createCookieCutterMesh(simplified, {
        height: ccHeight,
        thickness: ccThickness,
        handleStyle: ccHandle,
        handleHeight: ccHandleHeight,
        scale: ccScale / 100
      })
      modelGroupRef.current.add(mesh)
      resetCamera(150)
      setStatusMessage(`✅ Cookie cutter ready! ${simplified.length} points, ${ccHeight}mm tall`)
    } catch (err) {
      console.error('Cookie cutter generation error:', err)
      setStatusMessage(`❌ Error: ${err.message}`)
    } finally {
      setIsGenerating(false)
    }
  }, [getCanvasImageData, ccHeight, ccThickness, ccHandle, ccHandleHeight, ccScale, ccThreshold, clearModel, resetCamera])

  // --- Shadow Box Generation ---
  const generateShadowBox = useCallback(async () => {
    setIsGenerating(true)
    setStatusMessage('Separating layers...')

    try {
      const { data, width, height } = await getCanvasImageData()

      clearModel()
      const mesh = createShadowBoxMesh(data, width, height, {
        numLayers: sbLayers,
        layerSpacing: sbSpacing,
        showFrame: sbFrame,
        frameWidth: sbFrameWidth,
        boxWidth: sbSize,
        boxHeight: sbSize
      })
      modelGroupRef.current.add(mesh)
      resetCamera(250)
      setStatusMessage(`✅ Shadow box ready! ${sbLayers} layers, ${sbSpacing}mm spacing`)
    } catch (err) {
      console.error('Shadow box generation error:', err)
      setStatusMessage(`❌ Error: ${err.message}`)
    } finally {
      setIsGenerating(false)
    }
  }, [getCanvasImageData, sbLayers, sbSpacing, sbFrame, sbFrameWidth, sbSize, clearModel, resetCamera])

  // --- Book Nook Generation ---
  const generateBookNook = useCallback(async () => {
    setIsGenerating(true)
    setStatusMessage('Building layers...')

    try {
      clearModel()

      // Get canvas image for back wall
      let backImageUrl = null
      if (canvasImage) {
        backImageUrl = canvasImage
      } else if (canvasRef?.current) {
        try { backImageUrl = canvasRef.current.toDataURL({ format: 'png' }) } catch (e) { /* ignore */ }
      }

      const mesh = createBookNookLayers({
        theme: bnTheme,
        width: bnWidth,
        height: bnHeight,
        depth: bnDepth,
        materialThickness: bnMaterialThickness,
        showLighting: bnLighting,
        backImage: backImageUrl
      })
      modelGroupRef.current.add(mesh)
      resetCamera(350)

      const design = BOOK_NOOK_DESIGNS[bnTheme]
      const numLayers = design.getLayers(bnWidth, bnHeight).length
      setStatusMessage(`✅ ${design.name} — ${numLayers} layers + back wall | ${bnWidth}×${bnHeight}×${bnDepth}mm`)
    } catch (err) {
      console.error('Book nook generation error:', err)
      setStatusMessage(`❌ Error: ${err.message}`)
    } finally {
      setIsGenerating(false)
    }
  }, [bnTheme, bnWidth, bnHeight, bnDepth, bnLighting, bnMaterialThickness, canvasImage, canvasRef, clearModel, resetCamera])

  // --- 3D Relief Engraving Preview ---
  // Hits /api/export/to-depth-map to get a grayscale PNG, then renders a
  // high-resolution plane with `displacementMap` so the depth interpretation
  // matches what the M1 Ultra will physically engrave: dark = deeper carve.
  const generateRelief = useCallback(async () => {
    setIsGenerating(true)
    setStatusMessage('Generating depth map...')
    try {
      // Get the design source as a data URL — full quality, not downsampled
      let imageUrl = canvasImage
      if (!imageUrl && canvasRef?.current) {
        try { imageUrl = canvasRef.current.toDataURL({ format: 'png', multiplier: 1 }) } catch (e) {}
      }
      if (!imageUrl) {
        setStatusMessage('⚠️ Put a design on your canvas first.')
        setIsGenerating(false)
        return
      }

      const API_URL = import.meta.env.VITE_API_URL || '/api'
      const resp = await fetch(`${API_URL}/export/to-depth-map`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl,
          mode: reliefAiMode ? 'ai' : 'luminance',
          invert: false,
          blurPx: 3,
          floor: 30,
        }),
      })
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}))
        throw new Error(e.error || `HTTP ${resp.status}`)
      }
      const data = await resp.json()

      setStatusMessage('Building relief mesh...')
      // Load the returned data-URL into an Image, then a Three.js Texture
      const img = await new Promise((resolve, reject) => {
        const im = new Image()
        im.onload = () => resolve(im)
        im.onerror = reject
        im.src = data.depthMapBase64
      })

      clearModel()

      const depthTex = new THREE.Texture(img)
      depthTex.needsUpdate = true
      depthTex.colorSpace = THREE.NoColorSpace
      depthTex.minFilter = THREE.LinearFilter
      depthTex.magFilter = THREE.LinearFilter

      const aspect = img.height / img.width
      const planeW = reliefWidthMm
      const planeH = reliefWidthMm * aspect
      // 256x256 segments gives ~65k vertices — enough to resolve fine engrave
      // detail without melting weak GPUs.
      const segW = 256
      const segH = Math.max(32, Math.round(256 * aspect))
      const geom = new THREE.PlaneGeometry(planeW, planeH, segW, segH)

      const matPresets = {
        wood:    { color: 0xa0764b, roughness: 0.82, metalness: 0.05 },
        acrylic: { color: 0x1a1a1a, roughness: 0.25, metalness: 0.15 },
        slate:   { color: 0x2c3340, roughness: 0.95, metalness: 0.02 },
      }
      const matCfg = matPresets[reliefMaterial] || matPresets.wood

      const material = new THREE.MeshStandardMaterial({
        color: matCfg.color,
        roughness: matCfg.roughness,
        metalness: matCfg.metalness,
        displacementMap: depthTex,
        // Negative scale + matching bias: white pixels (255) sit at base level
        // (z=0), dark pixels (0) push DOWN by reliefMaxDepthMm — matches the
        // physical engrave direction (material removed = below surface).
        displacementScale: reliefMaxDepthMm,
        displacementBias: -reliefMaxDepthMm,
        side: THREE.DoubleSide,
      })

      const mesh = new THREE.Mesh(geom, material)
      mesh.rotation.x = -Math.PI / 2  // lay flat like a cutting board
      mesh.castShadow = true
      mesh.receiveShadow = true
      modelGroupRef.current.add(mesh)
      resetCamera(Math.max(planeW, planeH) * 1.5)

      setStatusMessage(
        `✅ Relief preview ready! ${planeW.toFixed(0)}×${planeH.toFixed(0)}mm × ` +
        `${reliefMaxDepthMm}mm deep | ${reliefMaterial} | ` +
        `${reliefAiMode ? 'AI depth' : 'luminance'} | ${data.stats?.depth_map || ''}`
      )
    } catch (err) {
      console.error('Relief preview error:', err)
      setStatusMessage(`❌ Error: ${err.message}`)
    } finally {
      setIsGenerating(false)
    }
  }, [canvasImage, canvasRef, reliefAiMode, reliefMaxDepthMm, reliefMaterial, reliefWidthMm, clearModel, resetCamera])

  // --- Book Nook SVG Export ---
  const handleExportBookNookSVG = useCallback(() => {
    try {
      const result = exportBookNookSVGs({
        theme: bnTheme,
        width: bnWidth,
        height: bnHeight,
        depth: bnDepth,
        materialThickness: bnMaterialThickness
      })
      setStatusMessage(`✅ ${result}`)
    } catch (err) {
      setStatusMessage(`❌ SVG export error: ${err.message}`)
    }
  }, [bnTheme, bnWidth, bnHeight, bnDepth, bnMaterialThickness])

  // --- Export ---
  const handleExportSTL = useCallback(() => {
    if (!modelGroupRef.current || modelGroupRef.current.children.length === 0) {
      setStatusMessage('⚠️ Nothing to export. Generate a model first!')
      return
    }
    const filename = `zenna-${mode}-${Date.now()}.stl`
    exportSTL(modelGroupRef.current, filename)
    setStatusMessage(`✅ Exported: ${filename}`)
  }, [mode])

  // --- Mode change ---
  useEffect(() => {
    if (onModeChange) onModeChange(mode)
    setStatusMessage('')
  }, [mode, onModeChange])

  // Update mode from prop
  useEffect(() => {
    if (initialMode && initialMode !== mode) {
      setMode(initialMode)
    }
  }, [initialMode])

  // ============================================================================
  // RENDER
  // ============================================================================

  // Shared slider component
  const Slider = ({ label, value, onChange, min, max, step = 1, unit = '' }) => (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-400">{label}</span>
        <span className="text-zenna-cyan font-mono">{value}{unit}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-zenna-violet"
      />
    </div>
  )

  // Generate button
  const GenerateBtn = ({ onClick, label = 'Generate', disabled = false }) => (
    <button
      onClick={onClick}
      disabled={disabled || isGenerating || (!canvasImage && !canvasRef?.current)}
      className="w-full py-2.5 rounded-xl font-semibold text-sm text-white
        bg-gradient-to-r from-zenna-violet via-zenna-cyan to-zenna-emerald
        hover:opacity-90 active:scale-[0.98] transition-all
        disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {isGenerating ? '⏳ Working...' : label}
    </button>
  )

  return (
    <div className="flex flex-col h-full bg-slate-900 rounded-xl overflow-hidden border border-slate-700/50">

      {/* Mode Tabs */}
      <div className="flex border-b border-slate-700/50 bg-slate-800/50 flex-shrink-0">
        {Object.entries(MODE_LABELS).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setMode(key)}
            className={`flex-1 px-2 py-2.5 text-xs font-medium transition-all
              ${mode === key
                ? 'text-white bg-gradient-to-b from-zenna-violet/20 to-transparent border-b-2 border-zenna-violet'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
              }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-1 overflow-hidden">

        {/* Controls Panel (left side) */}
        <div className="w-56 flex-shrink-0 border-r border-slate-700/50 bg-slate-800/30 overflow-y-auto p-3 space-y-3">

          {/* === VIEWER MODE CONTROLS === */}
          {mode === MODES.VIEWER && (
            <>
              <div className="text-xs text-slate-300 font-semibold uppercase tracking-wider mb-2">Display</div>

              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                <input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-700 text-zenna-violet focus:ring-zenna-violet" />
                Show Grid
              </label>

              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                <input type="checkbox" checked={showWireframe} onChange={(e) => setShowWireframe(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-700 text-zenna-violet focus:ring-zenna-violet" />
                Wireframe
              </label>

              <button
                onClick={() => resetCamera()}
                className="w-full py-2 rounded-lg text-xs font-medium text-slate-300 border border-slate-600 hover:bg-slate-700 transition-colors"
              >
                🎯 Reset Camera
              </button>

              <div className="border-t border-slate-700/50 pt-3">
                <div className="text-xs text-slate-300 font-semibold uppercase tracking-wider mb-2">Export</div>
                <button
                  onClick={handleExportSTL}
                  className="w-full py-2 rounded-lg text-xs font-medium text-white bg-zenna-emerald/80 hover:bg-zenna-emerald transition-colors"
                >
                  📥 Export STL
                </button>
              </div>

              <div className="border-t border-slate-700/50 pt-3">
                <div className="text-xs text-slate-500">
                  <p className="mb-1"><strong>Controls:</strong></p>
                  <p>🖱️ Left drag — Rotate</p>
                  <p>🖱️ Right drag — Pan</p>
                  <p>🔲 Scroll — Zoom</p>
                </div>
              </div>
            </>
          )}

          {/* === COOKIE CUTTER CONTROLS === */}
          {mode === MODES.COOKIE_CUTTER && (
            <>
              <div className="text-xs text-slate-300 font-semibold uppercase tracking-wider mb-2">Cookie Cutter</div>

              {!canvasImage && !canvasRef?.current && (
                <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-xs text-amber-400">⚠️ Put a design on your canvas first, then switch to 3D View</p>
                </div>
              )}

              <Slider label="Outline Threshold" value={ccThreshold} onChange={setCcThreshold} min={50} max={220} />
              <Slider label="Wall Height" value={ccHeight} onChange={setCcHeight} min={8} max={30} unit="mm" />
              <Slider label="Wall Thickness" value={ccThickness} onChange={setCcThickness} min={0.6} max={3} step={0.1} unit="mm" />
              <Slider label="Scale" value={ccScale} onChange={setCcScale} min={50} max={200} unit="%" />

              <div className="mb-3">
                <div className="text-xs text-slate-400 mb-1.5">Handle Style</div>
                <div className="grid grid-cols-3 gap-1">
                  {Object.entries({ none: 'None', rim: 'Rim', bridge: 'Bar' }).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setCcHandle(key)}
                      className={`py-1.5 rounded-lg text-[10px] font-medium transition-all
                        ${ccHandle === key
                          ? 'bg-zenna-violet text-white'
                          : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                        }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {ccHandle !== HANDLE_STYLES.NONE && (
                <Slider label="Handle Height" value={ccHandleHeight} onChange={setCcHandleHeight} min={3} max={15} unit="mm" />
              )}

              <GenerateBtn onClick={generateCookieCutter} label="🍪 Generate Cookie Cutter" />

              <div className="border-t border-slate-700/50 pt-3">
                <button
                  onClick={handleExportSTL}
                  className="w-full py-2 rounded-lg text-xs font-medium text-white bg-zenna-emerald/80 hover:bg-zenna-emerald transition-colors"
                >
                  📥 Export STL
                </button>
              </div>

              <div className="text-[10px] text-slate-500 mt-2">
                <p>💡 <strong>Print tip:</strong> PLA, 0.2mm layers, 0% infill, 2-3 walls. Seal with food-grade epoxy for food safety.</p>
              </div>
            </>
          )}

          {/* === SHADOW BOX CONTROLS === */}
          {mode === MODES.SHADOW_BOX && (
            <>
              <div className="text-xs text-slate-300 font-semibold uppercase tracking-wider mb-2">Shadow Box</div>

              {!canvasImage && !canvasRef?.current && (
                <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-xs text-amber-400">⚠️ Put a design on your canvas first, then switch to 3D View</p>
                </div>
              )}

              <Slider label="Number of Layers" value={sbLayers} onChange={setSbLayers} min={2} max={6} />
              <Slider label="Layer Spacing" value={sbSpacing} onChange={setSbSpacing} min={3} max={20} unit="mm" />
              <Slider label="Box Size" value={sbSize} onChange={setSbSize} min={80} max={300} unit="mm" />

              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer mb-3">
                <input type="checkbox" checked={sbFrame} onChange={(e) => setSbFrame(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-700 text-zenna-violet focus:ring-zenna-violet" />
                Show Frame
              </label>

              {sbFrame && (
                <Slider label="Frame Width" value={sbFrameWidth} onChange={setSbFrameWidth} min={5} max={30} unit="mm" />
              )}

              <GenerateBtn onClick={generateShadowBox} label="🖼️ Generate Shadow Box" />

              <div className="border-t border-slate-700/50 pt-3">
                <button
                  onClick={handleExportSTL}
                  className="w-full py-2 rounded-lg text-xs font-medium text-white bg-zenna-emerald/80 hover:bg-zenna-emerald transition-colors"
                >
                  📥 Export STL
                </button>
              </div>

              <div className="text-[10px] text-slate-500 mt-2">
                <p>💡 <strong>Tip:</strong> Each layer can be laser-cut from different colored cardstock, or 3D printed as one piece.</p>
              </div>
            </>
          )}

          {/* === BOOK NOOK CONTROLS === */}
          {mode === MODES.BOOK_NOOK && (
            <>
              <div className="text-xs text-slate-300 font-semibold uppercase tracking-wider mb-2">Book Nook</div>

              <div className="mb-3">
                <div className="text-xs text-slate-400 mb-1.5">Theme</div>
                <div className="space-y-1">
                  {Object.entries(BOOK_NOOK_DESIGNS).map(([key, design]) => (
                    <button
                      key={key}
                      onClick={() => setBnTheme(key)}
                      className={`w-full text-left px-2 py-1.5 rounded-lg text-[11px] transition-all
                        ${bnTheme === key
                          ? 'bg-zenna-violet/20 text-white border border-zenna-violet/40'
                          : 'text-slate-400 hover:bg-slate-700/50 border border-transparent'
                        }`}
                    >
                      <span className="font-medium">{design.name}</span>
                      {bnTheme === key && (
                        <p className="text-[9px] text-slate-500 mt-0.5">{design.description}</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="text-xs text-slate-300 font-semibold uppercase tracking-wider mb-2">Dimensions</div>

              <Slider label="Width" value={bnWidth} onChange={setBnWidth} min={100} max={250} unit="mm" />
              <Slider label="Height" value={bnHeight} onChange={setBnHeight} min={120} max={300} unit="mm" />
              <Slider label="Depth" value={bnDepth} onChange={setBnDepth} min={80} max={250} unit="mm" />

              <div className="text-xs text-slate-300 font-semibold uppercase tracking-wider mb-2 mt-3">Material</div>
              <Slider label="Thickness" value={bnMaterialThickness} onChange={setBnMaterialThickness} min={2} max={6} unit="mm" />

              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer mb-3">
                <input type="checkbox" checked={bnLighting} onChange={(e) => setBnLighting(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-700 text-zenna-violet focus:ring-zenna-violet" />
                Interior Lighting Preview
              </label>

              <GenerateBtn onClick={generateBookNook} label="📚 Generate Book Nook" />

              {canvasImage && (
                <p className="text-[10px] text-zenna-cyan mt-1">✨ Canvas image → back wall artwork</p>
              )}

              <div className="border-t border-slate-700/50 pt-3 mt-3 space-y-2">
                <button
                  onClick={handleExportBookNookSVG}
                  className="w-full py-2 rounded-lg text-xs font-medium text-white bg-red-600/80 hover:bg-red-600 transition-colors"
                >
                  ✂️ Export SVG — Laser Cutter
                </button>
                <button
                  onClick={handleExportSTL}
                  className="w-full py-2 rounded-lg text-xs font-medium text-white bg-zenna-emerald/80 hover:bg-zenna-emerald transition-colors"
                >
                  📥 Export STL — 3D Print
                </button>
              </div>

              <div className="text-[10px] text-slate-500 mt-2 space-y-1">
                <p>✂️ <strong>SVG export:</strong> Red = cut lines, Blue = engrave/labels. All layers laid out on one sheet.</p>
                <p>🪵 <strong>Assembly:</strong> Stack layers front-to-back with spacers between. Attach LED strip at top. Print canvas art on back panel.</p>
              </div>
            </>
          )}

          {/* === 3D ENGRAVE RELIEF CONTROLS === */}
          {mode === MODES.RELIEF && (
            <>
              <div className="text-xs text-slate-300 font-semibold uppercase tracking-wider mb-2">3D Engrave preview</div>

              {!canvasImage && !canvasRef?.current && (
                <p className="text-xs text-amber-400 mb-2">⚠️ Put a design on your canvas first, then switch to 3D View</p>
              )}

              <div className="text-xs text-slate-400 mb-1.5">Material</div>
              <div className="grid grid-cols-3 gap-1 mb-3">
                {[
                  { key: 'wood', label: '🪵 Wood' },
                  { key: 'acrylic', label: '🪞 Acrylic' },
                  { key: 'slate', label: '🪨 Slate' },
                ].map(m => (
                  <button
                    key={m.key}
                    onClick={() => setReliefMaterial(m.key)}
                    className={`px-2 py-1.5 rounded-lg text-[11px] transition-all
                      ${reliefMaterial === m.key
                        ? 'bg-zenna-violet/20 text-white border border-zenna-violet/40'
                        : 'text-slate-400 hover:bg-slate-700/50 border border-transparent'}`}
                  >{m.label}</button>
                ))}
              </div>

              <Slider label="Width" value={reliefWidthMm} onChange={setReliefWidthMm} min={50} max={400} unit="mm" />
              <Slider label="Max carve depth" value={reliefMaxDepthMm} onChange={setReliefMaxDepthMm} min={0.5} max={8} step={0.5} unit="mm" />

              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer mb-3 mt-2">
                <input type="checkbox" checked={reliefAiMode} onChange={(e) => setReliefAiMode(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-700 text-zenna-violet focus:ring-zenna-violet" />
                AI depth (Depth Anything) — for photos
              </label>
              <p className="text-[10px] text-slate-500 mb-3">
                Off (default) = luminance: dark pixels carve deeper. Right for line art / posterized designs.<br/>
                On = AI monocular depth. Right for photos where you want true spatial relief.
              </p>

              <GenerateBtn onClick={generateRelief} label="🪵 Preview 3D Engrave" />

              <div className="text-[10px] text-slate-500 mt-2 space-y-1">
                <p>🪵 <strong>Convention:</strong> Dark = carved deep, white = surface untouched.</p>
                <p>📤 Use the canvas Export menu → <strong>3D Engrave depth (line art or photo / AI)</strong> to download the depth PNG and import into xCS as "3D Curve" engraving.</p>
              </div>
            </>
          )}

          {/* Shared toggles for non-viewer modes */}
          {mode !== MODES.VIEWER && (
            <div className="border-t border-slate-700/50 pt-3">
              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                <input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-700 text-zenna-violet focus:ring-zenna-violet" />
                Grid
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer mt-1.5">
                <input type="checkbox" checked={showWireframe} onChange={(e) => setShowWireframe(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-700 text-zenna-violet focus:ring-zenna-violet" />
                Wireframe
              </label>
              <button
                onClick={() => resetCamera(mode === MODES.BOOK_NOOK ? 350 : mode === MODES.SHADOW_BOX ? 250 : 150)}
                className="w-full py-1.5 mt-2 rounded-lg text-[10px] font-medium text-slate-400 border border-slate-700 hover:bg-slate-700 transition-colors"
              >
                🎯 Reset Camera
              </button>
            </div>
          )}

        </div>

        {/* 3D Canvas */}
        <div className="flex-1 relative">
          <div ref={containerRef} className="absolute inset-0" />

          {/* Status bar */}
          {statusMessage && (
            <div className="absolute bottom-3 left-3 right-3 px-3 py-2 rounded-lg bg-slate-800/90 backdrop-blur-sm border border-slate-700/50">
              <p className="text-xs text-slate-300">{statusMessage}</p>
            </div>
          )}

          {/* Empty state overlay */}
          {modelGroupRef.current?.children.length === 0 && !isGenerating && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center p-6">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-zenna-violet/20 to-zenna-cyan/20 flex items-center justify-center">
                  <span className="text-2xl">
                    {mode === MODES.COOKIE_CUTTER ? '🍪' : mode === MODES.SHADOW_BOX ? '🖼️' : mode === MODES.BOOK_NOOK ? '📚' : '🧊'}
                  </span>
                </div>
                <p className="text-sm text-slate-400 font-medium">
                  {mode === MODES.VIEWER && 'Generate a 3D model to view it here'}
                  {mode === MODES.COOKIE_CUTTER && 'Put a design on canvas, then click Generate'}
                  {mode === MODES.SHADOW_BOX && 'Put a design on canvas, then click Generate'}
                  {mode === MODES.BOOK_NOOK && 'Pick a theme and click Generate'}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Use the controls on the left to configure your model
                </p>
              </div>
            </div>
          )}

          {/* Generating overlay */}
          {isGenerating && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-zenna-violet rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-zenna-cyan rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-zenna-emerald rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                <span className="text-sm text-slate-300 ml-2">{statusMessage || 'Generating...'}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}